/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Grid from '@mui/material/Grid';
import Menu from '@mui/material/Menu';
import TableContainer from '@mui/material/TableContainer';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import FormQuantity from 'components/Form/FormQuantity';
import FormPrice from 'components/Form/FormPrice';
import FormDiscount from 'components/Form/FormDiscount';
import FormDiscount2 from 'components/Form/FormDiscount2';
import FormFree from 'components/Form/FormFree';
import MDInput from 'components/MDInput';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

import SearchProduct from './SearchProduct';
import PurchaseOrderRequest from './PurchaseOrderRequest';
import SearchPr from './SearchPr';
import ImportExcel from './ImportExcel';
import ImportText from './ImportText';
import SearchMinMax from './SearchMinMax';

const initParent = {
  id: '',
  code: '',
  remark: '',
  type_id: '',
  supplier_id: '',
  discount_type: 'B',
  discount_value: '',
  discount_percent: '',
  shipping: '',
  discount_unit: 'Y',
  approve: 'N',
  vat_id: '2',
  status: 'edit',
  deposit: '',
  print_url: '',
  created_at: '',
  updated_at: '',
};

const initPhoto = {
  id: '',
  code: '',
  sku: '',
  name: '',
  image: '',
};

const PurchaseOrderList = forwardRef(({ setLimitApprove }, ref) => {
  const mod = 'purchase_order_list';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [record, setRecord] = useState([]);
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [parent, setParent] = useState(initParent);
  const [discountChange, setDiscountChange] = useState('');
  const [percentChange, setPercentChange] = useState('');
  const [shippingChange, setShippingChange] = useState('');
  const [depositChange, setDepositChange] = useState('');
  const [buttonGroup, setButtonGroup] = useState(null);
  const [modalPhoto, setModalPhoto] = useState(false);
  const [photo, setPhoto] = useState(initPhoto);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  const view = () => {
    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      ref_id: id,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setParent(result.parent);
        setDiscountChange(result.parent.discount_value);
        setPercentChange(result.parent.discount_percent);
        setShippingChange(result.parent.shipping);
        setLimitApprove(result.parent.approve);
        setLoading(false);
      }
    });
  };

  const add = (item) => {
    setLoading(true);

    FetchData('PUT', `/${mod}`, app.token, {
      po_id: id,
      list_id: item.list_id,
      barcode_id: item.barcode_id,
      quantity: item.quantity,
      quantity_balance: item.quantity,
      unit_id: item.unit_id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const importData = (item) => {
    setLoading(true);
    setRecord([]);

    FetchData('OPTIONS', `/${mod}/import`, app.token, {
      po_id: id,
      list: item,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const importQuotation = () => {
    setButtonGroup(null);
    setLoading(true);
    setRecord([]);

    FetchData('OPTIONS', `/${mod}/quotation`, app.token, {
      po_id: id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const importText = (text) => {
    setLoading(true);
    setRecord([]);

    FetchData('OPTIONS', `/${mod}/importtext`, app.token, {
      po_id: id,
      text: text,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const edit = (item, kind) => {
    FetchData('PATCH', `/${mod}/${item.id}`, app.token, {
      kind: kind,
      price: item.price,
      quantity: item.quantity,
      quantity_free: item.quantity_free,
      discount_value: item.discount_value,
      discount_type: item.discount_type,
    }).then((result) => {
      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editDiscountStep2 = (item, kind) => {
    FetchData('PATCH', `/${mod}/${item.id}`, app.token, {
      kind: `${kind}2`,
      price: item.price,
      quantity: item.quantity,
      quantity_free: item.quantity_free,
      discount_value2: item.discount_value2,
      discount_type2: item.discount_type2,
    }).then((result) => {
      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editDiscount = (item) => {
    FetchData('OPTIONS', `/${mod}/discount`, app.token, {
      ref_id: id,
      discount_value: item.discount_value,
      discount_type: 'B',
    }).then((result) => {
      if (result.status === 'ok') {
        setParent(result.parent);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editDiscountPercent = (item) => {
    FetchData('OPTIONS', `/${mod}/discount`, app.token, {
      ref_id: id,
      discount_percent: item.discount_percent,
      discount_type: 'P',
    }).then((result) => {
      if (result.status === 'ok') {
        setParent(result.parent);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const editDiscountUnit = (input) => {
    FetchData('OPTIONS', `/${mod}/editdiscount`, app.token, {
      id: parent.id,
      discount_unit: input,
    }).then((result) => {
      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.${result.msg}`), 'error');
      }
    });
  };

  const sendType = (type) => {
    setParent({ ...parent, discount_type: type });
    editDiscount({ discount_value: parent.discount_value, discount_type: type });
  };

  const sendValue = () => {
    if (parent.discount_value * 1 !== discountChange * 1) {
      setDiscountChange(parent.discount_value);
      editDiscount({ discount_value: parent.discount_value, discount_type: parent.discount_type });
    }
  };

  const sendPercent = () => {
    if (parent.discount_percent * 1 !== percentChange * 1) {
      setPercentChange(parent.discount_percent);
      editDiscountPercent({
        discount_percent: parent.discount_percent,
        discount_type: parent.discount_type,
      });
    }
  };

  const sendShipping = () => {
    if (parent.shipping * 1 === shippingChange * 1) return;

    setShippingChange(parent.shipping);

    FetchData('OPTIONS', `/${mod}/shipping`, app.token, {
      ref_id: id,
      shipping: parent.shipping,
    }).then((result) => {
      if (result.status === 'ok') {
        setParent(result.parent);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const sendDeposit = () => {
    if (parent.deposit * 1 === depositChange * 1) return;

    setDepositChange(parent.deposit);

    FetchData('OPTIONS', `/${mod}/deposit`, app.token, {
      ref_id: id,
      deposit: parent.deposit,
    }).then((result) => {
      if (result.status === 'ok') {
        setParent(result.parent);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const del = (item) => {
    setLoading(true);

    FetchData('DELETE', `/${mod}/${item.id}`, app.token, {}).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        view();
        showNotify(t(`alert.deletecomplete`), 'success');
      } else {
        showNotify(t(`alert.deleteerror`), 'error');
      }
    });
  };

  const btnDel = (item) => {
    if (parent.status !== 'edit' && parent.status !== 'waiting') return null;

    return (
      <MDButton
        variant='text'
        tabIndex={-1}
        color='error'
        size='small'
        iconOnly
        onClick={() => del(item)}
      >
        <Icon>delete</Icon>
      </MDButton>
    );
  };

  const btnDiscountUnit = () => {
    if (parent.status !== 'edit' && parent.status !== 'waiting') return null;

    if (parent.discount_unit === 'Y') {
      return (
        <MDButton variant='outlined' color='primary' onClick={() => editDiscountUnit('N')}>
          <Icon>local_offer</Icon> ลด / หน่วย
        </MDButton>
      );
    }

    return (
      <MDButton variant='outlined' color='warning' onClick={() => editDiscountUnit('Y')}>
        <Icon>discount</Icon> ลดรวม
      </MDButton>
    );
  };

  const renderHeaderEdit = () => {
    return (
      <TableHead>
        <TableRow>
          <TableCell align='center' width={10}>
            No.
          </TableCell>
          <TableCell align='center' width={40}>
            รูป
          </TableCell>
          <TableCell>สินค้า</TableCell>
          <TableCell align='center' width={120} className='nowrap'>
            ใบขอซื้อ
          </TableCell>
          <TableCell align='center' width={120} className='nowrap'>
            จำนวน
          </TableCell>
          <TableCell align='center' width={120} className='nowrap'>
            ราคาซื้อ
          </TableCell>
          <TableCell align='center' width={120} className='nowrap'>
            แถม
          </TableCell>
          <TableCell align='center' width={120} className='nowrap'>
            ลด STEP 1
          </TableCell>
          <TableCell align='center' width={120} className='nowrap'>
            ลด STEP 2
          </TableCell>
          <TableCell width={80}>หน่วย</TableCell>
          <TableCell align='right' width={100} className='nowrap'>
            ก่อนลด
          </TableCell>
          <TableCell align='right' width={100} className='nowrap'>
            ส่วนลด
          </TableCell>
          <TableCell align='right' width={100} className='nowrap'>
            ราคารวม
          </TableCell>
          <TableCell width={10}>&nbsp;</TableCell>
        </TableRow>
      </TableHead>
    );
  };

  const renderRecordEdit = () => {
    return (
      <TableBody>
        {loading ? (
          <TableRow>
            <TableCell colSpan={14} align='center' className='color-default'>
              กำลังโหลดข้อมูล ...
            </TableCell>
          </TableRow>
        ) : null}
        {record.map((row) => (
          <TableRow key={row.id}>
            <TableCell align='center'>{row.no}</TableCell>
            <TableCell
              onClick={() => {
                setPhoto(row);
                setModalPhoto(true);
              }}
            >
              {Func.imageDisplay(row)}
            </TableCell>
            <TableCell className='nowrap'>
              <MDBox className='nowrap'>
                {row.code} <span className='color-default'>{row.sku}</span>
              </MDBox>
              <MDBox className='cut' width={200}>
                {row.name}
              </MDBox>
            </TableCell>
            <TableCell align='center'>
              <PurchaseOrderRequest parent={row} />
            </TableCell>
            <TableCell align='right'>
              <FormQuantity parent={row} edit={edit} />
            </TableCell>
            <TableCell align='right'>
              <FormPrice parent={row} edit={edit} />
            </TableCell>
            <TableCell align='right'>
              <FormFree parent={row} edit={edit} />
            </TableCell>
            <TableCell align='right'>
              <FormDiscount parent={row} edit={edit} />
            </TableCell>
            <TableCell align='right'>
              <FormDiscount2 parent={row} edit={editDiscountStep2} />
            </TableCell>
            <TableCell align='left'>{row.unit_name}</TableCell>
            <TableCell align='right'>{Func.numberDisplay(row.total, 2)}</TableCell>
            <TableCell align='right'>
              {row.discount > 0 ? Func.numberDisplay(row.discount, 2) : '-'}
            </TableCell>
            <TableCell align='right'>{Func.numberDisplay(row.amount, 2)}</TableCell>
            <TableCell align='center'>{btnDel(row)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  };

  const renderHeader = () => {
    return (
      <TableHead>
        <TableRow>
          <TableCell align='center' width={20}>
            No.
          </TableCell>
          <TableCell align='center' width={40}>
            รูป
          </TableCell>
          <TableCell>สินค้า</TableCell>
          <TableCell align='center' className='nowrap'>
            ใบขอซื้อ
          </TableCell>
          <TableCell align='right' width={90} className='nowrap'>
            จำนวน
          </TableCell>
          <TableCell align='right' width={90} className='nowrap'>
            ราคาซื้อ
          </TableCell>
          <TableCell align='right' width={90} className='nowrap'>
            แถม
          </TableCell>
          <TableCell align='right' width={120} className='nowrap'>
            ลด STEP 1
          </TableCell>
          <TableCell align='right' width={120} className='nowrap'>
            ลด STEP 2
          </TableCell>
          <TableCell align='right' width={90} className='nowrap'>
            รับแล้ว
          </TableCell>
          <TableCell align='right' width={90} className='nowrap'>
            ค้างรับ
          </TableCell>
          <TableCell align='right' width={90}>
            ก่อนลด
          </TableCell>
          <TableCell align='right' width={90}>
            ส่วนลด
          </TableCell>
          <TableCell align='right' width={90}>
            รวม
          </TableCell>
        </TableRow>
      </TableHead>
    );
  };

  const renderRecord = () => {
    return (
      <TableBody>
        {loading ? (
          <TableRow>
            <TableCell colSpan={14} align='center' className='color-default'>
              กำลังโหลดข้อมูล ...
            </TableCell>
          </TableRow>
        ) : null}
        {record.map((row) => (
          <TableRow key={row.id}>
            <TableCell align='center'>{row.no}</TableCell>
            <TableCell
              align='center'
              onClick={() => {
                setPhoto(row);
                setModalPhoto(true);
              }}
            >
              {Func.imageDisplay(row)}
            </TableCell>
            <TableCell>
              <MDBox className='nowrap'>
                {row.code} <span className='color-default'>{row.sku}</span>
              </MDBox>
              <MDBox className='cut' width={200}>
                {row.name}
              </MDBox>
            </TableCell>
            <TableCell align='center'>
              <PurchaseOrderRequest parent={row} />
            </TableCell>
            <TableCell align='right'>
              <MDBox className='color-primary'>{Func.numberDisplay(row.quantity)}</MDBox>
              <MDBox>{row.unit_name}</MDBox>
            </TableCell>
            <TableCell align='right'>{row.price}</TableCell>
            <TableCell align='right'>
              <MDBox className='color-primary'>
                {row.quantity_free > 0 ? Func.numberDisplay(row.quantity_free) : '-'}
              </MDBox>
              <MDBox>{row.unit_name}</MDBox>
            </TableCell>
            <TableCell align='right' className='nowrap'>
              {row.discount_value > 0 ? (
                <span>
                  {Func.numberDisplay(row.discount_value)} {row.discount_type === 'P' ? '%' : '฿'}
                </span>
              ) : (
                '-'
              )}
            </TableCell>
            <TableCell align='right' className='nowrap'>
              {row.discount_value2 > 0 ? (
                <span>
                  {Func.numberDisplay(row.discount_value2)} {row.discount_type2 === 'P' ? '%' : '฿'}
                </span>
              ) : (
                '-'
              )}
            </TableCell>
            <TableCell align='right'>
              <MDBox className='color-success'>
                {row.quantity_receive > 0 ? Func.numberDisplay(row.quantity_receive) : '-'}
              </MDBox>
              <MDBox>{row.unit_name}</MDBox>
            </TableCell>
            <TableCell align='right'>
              <MDBox className='color-danger'>{Func.numberDisplay(row.quantity_balance)}</MDBox>
              <MDBox>{row.unit_name}</MDBox>
            </TableCell>
            <TableCell align='right'>{Func.numberDisplay(row.total, 2)}</TableCell>
            <TableCell align='right'>
              {row.discount > 0 ? Func.numberDisplay(row.discount, 2) : '-'}
            </TableCell>
            <TableCell align='right'>{Func.numberDisplay(row.amount, 2)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    );
  };

  const txtDiscount = () => {
    return (
      <MDBox
        noValidate
        component='form'
        role='form'
        onSubmit={(e) => {
          e.preventDefault();

          sendValue();
        }}
      >
        <MDInput
          onChange={(e) => setParent({ ...parent, discount_value: e.target.value })}
          onFocus={(e) => e.target.select()}
          onBlur={() => sendValue()}
          value={parent.discount_value}
          variant='standard'
          type='number'
          InputProps={{
            inputProps: {
              maxLength: 20,
              autoComplete: 'off',
              className: 'text-right',
              sx: { minWidth: 80, fontWeight: 'bold', fontSize: '1.3rem' },
            },
          }}
          fullWidth
        />
      </MDBox>
    );
  };

  const txtDiscountPercent = () => {
    return (
      <MDBox
        noValidate
        component='form'
        role='form'
        onSubmit={(e) => {
          e.preventDefault();

          sendPercent();
        }}
      >
        <MDInput
          onChange={(e) => setParent({ ...parent, discount_percent: e.target.value })}
          onFocus={(e) => e.target.select()}
          onBlur={() => sendPercent()}
          value={parent.discount_percent}
          variant='standard'
          type='number'
          InputProps={{
            inputProps: {
              maxLength: 20,
              autoComplete: 'off',
              className: 'text-right',
              sx: { minWidth: 80, fontWeight: 'bold', fontSize: '1.3rem' },
            },
          }}
          fullWidth
        />
      </MDBox>
    );
  };

  const txtShipping = () => {
    return (
      <MDBox
        noValidate
        component='form'
        role='form'
        onSubmit={(e) => {
          e.preventDefault();

          sendShipping();
        }}
      >
        <MDInput
          onChange={(e) => setParent({ ...parent, shipping: e.target.value })}
          onFocus={(e) => {
            e.target.select();
          }}
          onBlur={() => {
            sendShipping();
          }}
          value={parent.shipping}
          variant='standard'
          type='number'
          InputProps={{
            inputProps: {
              maxLength: 20,
              autoComplete: 'off',
              className: 'text-right',
              sx: { minWidth: 80, fontWeight: 'bold', fontSize: '1.3rem' },
            },
          }}
          fullWidth
        />
      </MDBox>
    );
  };

  const txtDeposit = () => {
    return (
      <MDBox
        noValidate
        component='form'
        role='form'
        onSubmit={(e) => {
          e.preventDefault();

          sendDeposit();
        }}
      >
        <MDInput
          onChange={(e) => setParent({ ...parent, deposit: e.target.value })}
          onFocus={(e) => {
            e.target.select();
          }}
          onBlur={() => {
            sendDeposit();
          }}
          value={parent.deposit}
          variant='standard'
          type='number'
          InputProps={{
            inputProps: {
              maxLength: 20,
              autoComplete: 'off',
              className: 'text-right',
              sx: { minWidth: 80, fontWeight: 'bold', fontSize: '1.3rem' },
            },
          }}
          fullWidth
        />
      </MDBox>
    );
  };

  const renderSummaryEdit = () => {
    return (
      <Table aria-label='summary table'>
        <TableBody>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>มูลค่าสินค้า</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.total)}</MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>หักส่วนลด</MDTypography>
            </TableCell>
            <TableCell align='right'>{txtDiscount()}</TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>หักส่วนลด</MDTypography>
            </TableCell>
            <TableCell align='right'>{txtDiscountPercent()}</TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>%</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>มูลค่าสินค้าหลังหักส่วนลด</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.amount, 2)}</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>
                ภาษีมูลค่าเพิ่ม {Func.numberDisplay(parent.vat_value, 0)}%
              </MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.vat, 2)}</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>มูลค่ารวมภาษี</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5' color='primary'>
                {Func.numberDisplay(parent.amount_vat, 2)}
              </MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>ค่าจัดส่ง</MDTypography>
            </TableCell>
            <TableCell align='right'>{txtShipping()}</TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>รวมทั้งสิ้น</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5' color='info'>
                {Func.numberDisplay(parent.nettotal, 2)}
              </MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>มัดจำ</MDTypography>
            </TableCell>
            <TableCell align='right'>{txtDeposit()}</TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  };

  const renderSummary = () => {
    return (
      <Table aria-label='summary table'>
        <TableBody>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>มูลค่าสินค้า</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.total, 2)}</MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>หักส่วนลด</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>
                {Func.numberDisplay(parent.discount_value, 2)}
              </MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>หักส่วนลด</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>
                {Func.numberDisplay(parent.discount_percent, 2)}
              </MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>%</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>มูลค่าสินค้าหลังหักส่วนลด</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.amount, 2)}</MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>
                ภาษีมูลค่าเพิ่ม {Func.numberDisplay(parent.vat_value, 0)}%
              </MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.vat, 2)}</MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>มูลค่ารวมภาษี</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5' color='primary'>
                {Func.numberDisplay(parent.amount_vat, 2)}
              </MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>ค่าจัดส่ง</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.shipping, 2)}</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>รวมทั้งสิ้น</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5' color='info'>
                {Func.numberDisplay(parent.nettotal, 2)}
              </MDTypography>
            </TableCell>
            <TableCell align='right' width={50}>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell align='right'>
              <MDTypography variant='h5'>มัดจำ</MDTypography>
            </TableCell>
            <TableCell align='right' width='200'>
              <MDTypography variant='h5'>{Func.numberDisplay(parent.deposit, 2)}</MDTypography>
            </TableCell>
            <TableCell align='right'>
              <MDTypography variant='h5'>บาท</MDTypography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  };

  const renderButton = () => {
    return (
      <MDBox display='flex' justifyContent='end' gap={1} p={2}>
        {btnDiscountUnit()}
        {parent.status !== 'edit' && parent.status !== 'waiting' ? null : (
          <MDButton
            variant='outlined'
            color='info'
            onClick={(e) => setButtonGroup(e.currentTarget)}
          >
            <Icon>add_circle_outline</Icon>&nbsp;เพิ่มสินค้า
          </MDButton>
        )}
        <Menu
          sx={{ mt: '45px' }}
          id='menu-appbar'
          anchorEl={buttonGroup}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          open={Boolean(buttonGroup)}
          onClose={() => setButtonGroup(null)}
        >
          <SearchMinMax add={add} setButtonGroup={setButtonGroup} />
          <SearchPr add={add} refresh={view} setButtonGroup={setButtonGroup} />
          <SearchProduct add={add} setButtonGroup={setButtonGroup} />
          {record.length > 0 ? (
            <MenuItem onClick={importQuotation}>
              <MDTypography variant='body2'>
                <Icon color='secondary'>request_quote</Icon>&nbsp;ใบขอราคา
              </MDTypography>
            </MenuItem>
          ) : null}
          <ImportExcel callback={importData} setButtonGroup={setButtonGroup} />
          <ImportText callback={importText} setButtonGroup={setButtonGroup} />
        </Menu>
        <MDButton
          variant='outlined'
          color='success'
          onClick={() => {
            setButtonGroup(null);
            window.open(parent.print, '_self');
          }}
        >
          <Icon>download</Icon>&nbsp;Excel
        </MDButton>
      </MDBox>
    );
  };

  const renderList = () => {
    return (
      <MDBox>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <MDBox pt={3} pl={2}>
              <MDTypography variant='h5'>{t(`element.order`)}</MDTypography>
            </MDBox>
          </Grid>

          <Grid item xs={6}>
            {renderButton()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer sx={{ boxShadow: 0, minHeight: 300 }}>
              <Table aria-label='simple table'>
                {parent.status === 'edit' || parent.status === 'waiting'
                  ? renderHeaderEdit()
                  : renderHeader()}
                {parent.status === 'edit' || parent.status === 'waiting'
                  ? renderRecordEdit()
                  : renderRecord()}
              </Table>
            </TableContainer>
            <TableContainer sx={{ boxShadow: 0, mt: 2 }}>
              {parent.status === 'edit' || parent.status === 'waiting'
                ? renderSummaryEdit()
                : renderSummary()}
            </TableContainer>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPhoto = () => {
    return (
      <Dialog
        open={modalPhoto}
        maxWidth='sm'
        keepMounted
        aria-describedby='dialog-image'
        fullWidth
        onClose={() => setModalPhoto(false)}
      >
        <DialogTitle>
          {photo.code} <span className='color-default'>{photo.sku}</span>
          <IconButton
            aria-label='close'
            onClick={() => setModalPhoto(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox mb={1}>
            <MDTypography variant='body'>{photo.name}</MDTypography>
          </MDBox>
          <MDBox
            component='img'
            src={photo.image}
            alt=''
            borderRadius='lg'
            width='100%'
            position='relative'
            sx={{ boxShadow: 0 }}
          />
        </DialogContent>
      </Dialog>
    );
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  useImperativeHandle(ref, () => {
    return {
      view,
    };
  });

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    view();
  }, [id]);

  return (
    <div>
      {renderList()}
      {renderLoading()}
      {renderPhoto()}
      {notify}
    </div>
  );
});

PurchaseOrderList.propTypes = {
  setLimitApprove: PropTypes.func.isRequired,
};

export default PurchaseOrderList;
